// Payroll Management Component — 8-Tab Architecture with Force Analytics & Direct Card Navigation
import React, { useState, useEffect } from 'react';
import SalaryStructureTab from './payroll/SalaryStructureTab';
import PayrollGroupsTab from './payroll/PayrollGroupsTab';
import AllowancesTab from './payroll/AllowancesTab';
import ProcessingTab from './payroll/ProcessingTab';
import ApprovalTab from './payroll/ApprovalTab';
import SalaryHoldsTab from './payroll/SalaryHoldsTab';
import OperationalCostsTab from './payroll/OperationalCostsTab';
import ReportsTab from './payroll/ReportsTab';
import { hasAnyRole } from '../utils/roleHelper';

const TABS = [
  { key: 'structure',   label: '📊 Salary Structure',     roles: ['admin', 'payroll_officer', 'finance_officer', 'finance_director', 'finance', 'director_general', 'accountant_general', 'auditor'] },
  { key: 'groups',      label: '👥 Payroll Groups',        roles: ['admin', 'payroll_officer', 'finance_officer', 'finance_director', 'finance', 'director_general', 'accountant_general'] },
  { key: 'allowances',  label: '💰 Allowances & Bonuses',  roles: ['admin', 'payroll_officer', 'finance_officer', 'finance_director', 'finance', 'director_general', 'accountant_general'] },
  { key: 'processing',  label: '⚙️ Payroll Processing',   roles: ['admin', 'payroll_officer', 'finance_officer', 'finance_director', 'finance', 'director_general', 'accountant_general'] },
  { key: 'approval',    label: '✅ Approval & Status',     roles: ['admin', 'payroll_officer', 'finance_officer', 'finance_director', 'finance', 'director_general', 'accountant_general', 'auditor'] },
  { key: 'holds',       label: '🔒 Salary Holds',          roles: ['admin', 'payroll_officer', 'finance_officer', 'finance_director', 'finance', 'director_general', 'accountant_general', 'commander', 'unit_commander', 'department_commander', 'army_commander'] },
  { key: 'operational', label: '📋 Operational Costs',     roles: ['admin', 'payroll_officer', 'finance_officer', 'finance_director', 'finance', 'director_general', 'accountant_general', 'auditor'] },
  { key: 'reports',     label: '📈 Reports',               roles: ['admin', 'payroll_officer', 'finance_officer', 'finance_director', 'finance', 'director_general', 'accountant_general', 'auditor'] },
];

const WORKFLOW_STEPS = [
  { step: '1', label: 'Force Payroll Officer', action: 'Generates Monthly Payroll (Draft)', icon: '📋', color: '#6b7280' },
  { step: '2', label: 'Finance Budget Office', action: 'Reviews & Verifies (L1)', icon: '🔍', color: '#0284c7' },
  { step: '3', label: 'MoF Director General', action: 'Approves (L2)', icon: '✔️', color: '#059669' },
  { step: '4', label: 'Accountant General', action: 'Final Approval & Locks (L3)', icon: '🔒', color: '#7c3aed' },
  { step: '5', label: 'Finance Budget Office', action: 'Generates Bank Transfer Files & Authorization Letter', icon: '📄', color: '#92400e' },
  { step: '6', label: 'Bank', action: 'Executes Payment', icon: '🏦', color: '#166534' },
];

const extractArray = (res, key) => {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  const payload = res.data !== undefined ? res.data : res;
  if (Array.isArray(payload)) return payload;
  if (key && Array.isArray(payload[key])) return payload[key];
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.army_personnel)) return payload.army_personnel;
  if (Array.isArray(payload.payrolls)) return payload.payrolls;
  if (Array.isArray(payload.salary_holds)) return payload.salary_holds;
  return [];
};

const getForceImage = (armyType) => {
  const name = (armyType?.name || '').toLowerCase();
  const code = (armyType?.code || '').toLowerCase();

  if (name.includes('police') || code.includes('ppf')) {
    return '/images/Police_army.jpeg';
  } else if (name.includes('darawish') || name.includes('darawiish') || name.includes('military') || code.includes('pmf')) {
    return '/images/Darawish_army.jpg';
  } else if (name.includes('custodial') || name.includes('asluubta') || code.includes('pcc')) {
    return '/images/Custodial_army.jpg';
  }
  return '/images/logo.png';
};

const Payroll = ({ api, user, subView }) => {
  const [selectedForce, setSelectedForce] = useState('');
  const [activeTab, setActiveTab] = useState('structure');
  const [armyTypes, setArmyTypes] = useState([]);
  const [groups, setGroups] = useState([]);
  const [showWorkflow, setShowWorkflow] = useState(false);

  // Analytics Metrics State
  const [analytics, setAnalytics] = useState({
    totalPersonnel: 0,
    totalBasicSalary: 0,
    totalAllowances: 0,
    totalBonuses: 0,
    totalDeductions: 0,
    totalNetSalary: 0,
    salaryHolds: 0,
    payrollStatus: 'DRAFT',
    loading: false
  });

  const selectForceBySubView = (atList, sub) => {
    if (!atList || atList.length === 0) return '';
    if (!sub) return String(atList[0].id);
    const subLower = sub.toLowerCase();
    const found = atList.find(at => {
      const name = (at.name || '').toLowerCase();
      const code = (at.code || '').toLowerCase();
      if (subLower === 'military' && (name.includes('military') || name.includes('darawiish') || name.includes('darawish') || code.includes('pmf'))) return true;
      if (subLower === 'police' && (name.includes('police') || code.includes('ppf'))) return true;
      if (subLower === 'discipline' && (name.includes('custodial') || name.includes('asluubta') || code.includes('pcc'))) return true;
      return false;
    });
    return found ? String(found.id) : String(atList[0].id);
  };

  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const [atRes, grRes] = await Promise.all([
          api.get('/army_types'),
          api.get('/payroll_groups')
        ]);
        const atList = extractArray(atRes, 'army_types');
        const grList = extractArray(grRes, 'payroll_groups');
        setArmyTypes(atList);
        setGroups(grList);
        const targetId = selectForceBySubView(atList, subView);
        if (targetId) setSelectedForce(targetId);
      } catch (e) { console.error('Error fetching metadata:', e); }
    };
    fetchMeta();
  }, [subView]);

  // Update selectedForce if subView changes dynamically
  useEffect(() => {
    if (armyTypes.length > 0 && subView) {
      const targetId = selectForceBySubView(armyTypes, subView);
      if (targetId) setSelectedForce(targetId);
    }
  }, [subView, armyTypes]);

  // Fetch Force Payroll Analytics
  useEffect(() => {
    if (!selectedForce) return;
    const fetchAnalytics = async () => {
      setAnalytics(prev => ({ ...prev, loading: true }));
      try {
        const [repRes, holdsRes, payRes, persRes] = await Promise.all([
          api.get(`/reports/payroll?report_type=salary&army_type_id=${selectedForce}`).catch(() => null),
          api.get(`/salary_holds?army_type_id=${selectedForce}`).catch(() => null),
          api.get(`/payrolls?army_type_id=${selectedForce}`).catch(() => null),
          api.get(`/army_personnel?army_type_id=${selectedForce}`).catch(() => null)
        ]);

        const repData = extractArray(repRes, 'data');
        const holdsData = extractArray(holdsRes, 'salary_holds');
        const payData = extractArray(payRes, 'payrolls');
        const persData = extractArray(persRes, 'army_personnel');

        let totalPersonnel = persData.length || repData.length;
        let totalBasic = 0;
        let totalAllow = 0;
        let totalBonus = 0;
        let totalDeduct = 0;
        let totalNet = 0;

        if (repData.length > 0) {
          repData.forEach(item => {
            totalBasic += parseFloat(item.salary || 0);
            const gxil = parseFloat(item.g_xil || 0);
            const gfarsamo = parseFloat(item.g_farsamo || 0);
            const ghawl = parseFloat(item.g_hawl || 0);
            totalAllow += (gxil + gfarsamo);
            totalBonus += ghawl;
            const mm = parseFloat(item.mm || 0);
            const bolo = parseFloat(item.bolo || 0);
            const health = parseFloat(item.health || 0);
            totalDeduct += (mm + bolo + health);
            totalNet += parseFloat(item.net_salary || 0);
          });
        } else {
          persData.forEach(p => {
            const sal = parseFloat(p?.salary_usd || 0);
            const all = parseFloat(p?.allowance_usd || 0);
            totalBasic += sal;
            totalAllow += all;
            totalNet += (sal + all);
          });
        }

        const activeHolds = holdsData.filter(h => (h.hold_status || h.status || '').toLowerCase() === 'active').length;
        const latestPayroll = payData[0];
        const statusStr = latestPayroll ? (latestPayroll.status || 'Draft').toUpperCase() : 'DRAFT';

        setAnalytics({
          totalPersonnel,
          totalBasicSalary: totalBasic,
          totalAllowances: totalAllow,
          totalBonuses: totalBonus,
          totalDeductions: totalDeduct,
          totalNetSalary: totalNet,
          salaryHolds: activeHolds,
          payrollStatus: statusStr,
          loading: false
        });
      } catch (err) {
        console.error('Error fetching analytics metrics:', err);
        setAnalytics(prev => ({ ...prev, loading: false }));
      }
    };
    fetchAnalytics();
  }, [selectedForce]);

  const visibleTabs = TABS.filter(t => hasAnyRole(user, t.roles));

  const hasAnyPayrollAccess = hasAnyRole(user, [
    'admin', 'payroll_officer', 'finance_officer', 'finance_director', 'finance',
    'director_general', 'accountant_general', 'auditor',
    'commander', 'unit_commander', 'department_commander', 'army_commander'
  ]);

  if (!hasAnyPayrollAccess) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 400 }}>
        <div style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
          <h2 style={{ color: '#dc2626', fontWeight: 800 }}>Access Restricted</h2>
          <p style={{ color: '#6b7280' }}>The Payroll Module is accessible to Finance and Command roles only.</p>
        </div>
      </div>
    );
  }

  const selectedForceObj = armyTypes.find(a => String(a.id) === String(selectedForce));

  const analyticsCards = [
    {
      key: 'personnel',
      title: 'Total Personnel',
      value: analytics.totalPersonnel.toLocaleString(),
      subtext: 'Personnel Payroll List',
      icon: '👥',
      color: '#1d4ed8',
      bgColor: '#eff6ff',
      targetTab: 'structure'
    },
    {
      key: 'basic_salary',
      title: 'Total Basic Salary',
      value: `$${analytics.totalBasicSalary.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      subtext: 'Salary Details',
      icon: '💵',
      color: '#059669',
      bgColor: '#ecfdf5',
      targetTab: 'structure'
    },
    {
      key: 'allowances',
      title: 'Total Allowances',
      value: `$${analytics.totalAllowances.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      subtext: 'Allowance Details',
      icon: '💰',
      color: '#d97706',
      bgColor: '#fffbeb',
      targetTab: 'allowances'
    },
    {
      key: 'net_salary',
      title: 'Total Net Salary',
      value: `$${analytics.totalNetSalary.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      subtext: 'Disbursement List',
      icon: '🏛️',
      color: '#0284c7',
      bgColor: '#f0f9ff',
      targetTab: 'processing'
    },
    {
      key: 'bonuses',
      title: 'Total Bonuses',
      value: `$${analytics.totalBonuses.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      subtext: 'Bonus Breakdown',
      icon: '🎁',
      color: '#7c3aed',
      bgColor: '#f5f3ff',
      targetTab: 'allowances'
    },
    {
      key: 'deductions',
      title: 'Total Deductions',
      value: `$${analytics.totalDeductions.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      subtext: 'Deduction Details',
      icon: '📉',
      color: '#dc2626',
      bgColor: '#fef2f2',
      targetTab: 'allowances'
    },
    {
      key: 'salary_holds',
      title: 'Salary Holds',
      value: `${analytics.salaryHolds} Active`,
      subtext: 'Salary Hold List',
      icon: '🔒',
      color: '#e11d48',
      bgColor: '#fff1f2',
      targetTab: 'holds'
    },
    {
      key: 'status',
      title: 'Payroll Status',
      value: analytics.payrollStatus,
      subtext: 'Approval & Workflow',
      icon: '⚡',
      color: '#4f46e5',
      bgColor: '#eef2ff',
      targetTab: 'approval'
    }
  ];

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {/* Page Header */}
      <div style={{ marginBottom: 20, padding: '20px 24px', background: 'linear-gradient(135deg, #1b4332 0%, #2d6a4f 100%)', borderRadius: 12, color: '#fff' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>
              💼 Payroll Management {selectedForceObj ? `— ${selectedForceObj.name}` : ''}
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: 13, opacity: 0.85 }}>
              Finance-controlled payroll — Puntland Security Forces &nbsp;|&nbsp; Ministry of Finance Compliant
            </p>
            {selectedForceObj && (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.2)', padding: '5px 14px', borderRadius: 20, marginTop: 10, border: '1px solid rgba(255,255,255,0.3)' }}>
                <img src={getForceImage(selectedForceObj)} alt={selectedForceObj.name} style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover', border: '1.5px solid #fff' }} />
                <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>Selected Force: {selectedForceObj.name}</span>
              </div>
            )}
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 12, opacity: 0.8 }}>Logged in as: {user?.email}</div>
            <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>Role: {user?.roles?.map(r => r.name).join(', ')}</div>
            <button onClick={() => setShowWorkflow(!showWorkflow)}
              style={{ marginTop: 6, padding: '4px 12px', background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 4, color: '#fff', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
              {showWorkflow ? '▲ Hide Workflow' : '▼ Show MoF Approval Flow'}
            </button>
          </div>
        </div>
      </div>

      {/* MoF Workflow Banner */}
      {showWorkflow && (
        <div style={{ marginBottom: 20, padding: '16px 20px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, overflowX: 'auto' }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#92400e', marginBottom: 12 }}>📋 OFFICIAL MoF PAYROLL APPROVAL WORKFLOW</div>
          <div style={{ display: 'flex', gap: 0, alignItems: 'stretch', minWidth: 700 }}>
            {WORKFLOW_STEPS.map((ws, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'stretch', flex: 1 }}>
                <div style={{ flex: 1, padding: '10px 12px', background: '#fff', border: `2px solid ${ws.color}`, borderRadius: i === 0 ? '8px 0 0 8px' : i === WORKFLOW_STEPS.length - 1 ? '0 8px 8px 0' : 0, textAlign: 'center' }}>
                  <div style={{ fontSize: 18, marginBottom: 4 }}>{ws.icon}</div>
                  <div style={{ fontSize: 10, fontWeight: 800, color: ws.color }}>STEP {ws.step}</div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginTop: 2 }}>{ws.label}</div>
                  <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>{ws.action}</div>
                </div>
                {i < WORKFLOW_STEPS.length - 1 && (
                  <div style={{ display: 'flex', alignItems: 'center', padding: '0 2px', color: '#fde68a', fontSize: 18, background: '#fffbeb' }}>→</div>
                )}
              </div>
            ))}
          </div>
          <div style={{ marginTop: 12, fontSize: 11, color: '#92400e' }}>
            ⚠ <strong>After Step 4 (Accountant General Final Approval):</strong> The payroll is permanently locked and cannot be edited. Any corrections must be made via a Supplementary Payroll.
          </div>
        </div>
      )}

      {/* Interactive Clickable Payroll Analytics Dashboard Grid */}
      {selectedForce && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#1e293b' }}>
              📈 Payroll Analytics Overview {selectedForceObj ? `(${selectedForceObj.name})` : ''}
            </h3>
            <span style={{ fontSize: 11, color: '#64748b' }}>💡 Click any metric card to open its detailed breakdown</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 12 }}>
            {analyticsCards.map(card => (
              <div
                key={card.key}
                onClick={() => setActiveTab(card.targetTab)}
                style={{
                  background: card.bgColor,
                  border: `1.5px solid ${card.color}30`,
                  borderRadius: 10,
                  padding: '14px 16px',
                  cursor: 'pointer',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = `0 6px 14px ${card.color}25`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)';
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                    {card.title}
                  </span>
                  <span style={{ fontSize: 18 }}>{card.icon}</span>
                </div>
                <div style={{ fontSize: 18, fontWeight: 800, color: card.color, marginBottom: 4 }}>
                  {analytics.loading ? '...' : card.value}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, color: '#64748b' }}>
                  <span>{card.subtext}</span>
                  <span style={{ fontWeight: 700, color: card.color }}>View →</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 0, borderBottom: '2px solid #e2e8f0', overflowX: 'auto', whiteSpace: 'nowrap' }}>
        {visibleTabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            style={{
              padding: '11px 16px', border: 'none',
              borderBottom: activeTab === t.key ? '3px solid #1b4332' : '3px solid transparent',
              background: activeTab === t.key ? '#fff' : 'none',
              fontWeight: activeTab === t.key ? 700 : 500,
              color: activeTab === t.key ? '#1b4332' : '#6b7280',
              cursor: 'pointer', fontSize: 12, marginBottom: -2, transition: 'all 0.2s', flexShrink: 0,
              borderRadius: '6px 6px 0 0'
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderTop: 'none', borderRadius: '0 0 8px 8px', padding: 20, minHeight: 400 }}>
        {armyTypes.length === 0 && (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
            <div>Loading security forces from database...</div>
          </div>
        )}
        {armyTypes.length > 0 && !selectedForce && (
          <div style={{ padding: 32, textAlign: 'center', color: '#6b7280' }}>Please select a force above to view payroll data.</div>
        )}
        {selectedForce && (() => {
          const at = armyTypes.find(a => String(a.id) === String(selectedForce));
          if (!at) return null;
          const fGroups = groups.filter(g => String(g.army_type_id) === String(at.id));
          return (
            <>
              {activeTab === 'structure' && <SalaryStructureTab api={api} user={user} armyTypeId={at.id} />}
              {activeTab === 'groups' && <PayrollGroupsTab api={api} user={user} armyTypeId={at.id} armyTypeName={at.name} />}
              {activeTab === 'allowances' && <AllowancesTab api={api} user={user} armyTypeId={at.id} groups={fGroups} />}
              {activeTab === 'processing' && <ProcessingTab api={api} user={user} armyTypeId={at.id} armyTypeName={at.name} groups={fGroups} />}
              {activeTab === 'approval' && <ApprovalTab api={api} user={user} armyTypeId={at.id} armyTypeName={at.name} />}
              {activeTab === 'holds' && <SalaryHoldsTab api={api} user={user} armyTypeId={at.id} armyTypeName={at.name} />}
              {activeTab === 'operational' && <OperationalCostsTab api={api} user={user} armyTypeId={at.id} armyTypeName={at.name} />}
              {activeTab === 'reports' && <ReportsTab api={api} user={user} armyTypeId={at.id} armyTypeName={at.name} />}
            </>
          );
        })()}
      </div>
    </div>
  );
};

export default Payroll;
